import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

const STATUS_LABELS = {
  0: 'חתום על הסכם ראשוני',
  1: 'חתום עם בעיית רישום על הסכם ראשוני',
  2: 'יש להשלים חתימה של הסכם ראשוני',
  3: 'דיור ציבורי',
  4: 'מסרב לשתף פעולה',
  5: 'בטיפול',
  6: 'לא נוצר קשר',
  7: 'חתום על הסכם סופי',
  8: 'חתום עם בעיית רישום',
  9: 'יש להשלים חתימה',
  10: 'קיבל הסכם',
  11: 'מגיע',
  12: 'יש לתאם חתימה',
  13: 'תואמה חתימה',
  14: 'רוצים לחתום אך יש בעיה משפטית',
  15: 'חתום על נון שופ - יהודה לוי',
  16: 'חתום על הסכם סופי - יהודה לוי',
  17: 'חתום על הסכם סופי עם הערת אזהרה - יהודה לוי',
  18: 'לא יודע על מה חתום - יהודה לוי',
  19: 'מגיע לכנס הצגת הסכם סקייליין',
  20: 'גר בחוץ לארץ',
};

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: 'white', borderRadius: 12, padding: '20px 24px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)',
      border: `2px solid ${color}`, flex: '1 1 160px', minWidth: 140
    }}>
      <div style={{ fontSize: '2rem', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#374151', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { projectName } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { statusLabel, tenants[], loading }

  useEffect(() => {
    api.get(`/reports/dashboard?projectName=${encodeURIComponent(projectName)}`)
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectName]);

  const openStatusModal = async (statusCode, statusLabel) => {
    setModal({ statusLabel, tenants: [], loading: true });
    try {
      const res = await api.get(`/reports/tenants-by-status?projectName=${encodeURIComponent(projectName)}&status=${statusCode}`);
      setModal({ statusLabel, tenants: res.data, loading: false });
    } catch {
      setModal({ statusLabel, tenants: [], loading: false });
    }
  };

  const pctRaw = stats ? ((stats.pure7Count || 0) + (stats.purePublicCount || 0) + (stats.mixed89Count || 0)) / stats.totalApartments * 100 : 0;
  const pct = Math.trunc(pctRaw * 100) / 100;
  const pctColor = pct >= 66.67 ? '#16a34a' : pct >= 50 ? '#ca8a04' : '#dc2626';
  const pctBg   = pct >= 66.67 ? '#dcfce7' : pct >= 50 ? '#fef9c3' : '#fee2e2';

  return (
    <div>
      <Navbar title={`דשבורד — ${decodeURIComponent(projectName)}`} breadcrumb="פרויקטים > דשבורד" />
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 className="page-title">סיכום פרויקט</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => navigate(`/buildings/${encodeURIComponent(projectName)}`)}>
              כניסה לבניינים
            </button>
            <button className="btn btn-secondary" onClick={() => navigate(`/reports/${encodeURIComponent(projectName)}`)}>
              דוחות
            </button>
            <button className="btn btn-secondary" onClick={() => navigate(`/project-hub/${projectName}`)}>← חזרה</button>
          </div>
        </div>

        {loading ? <p>טוען...</p> : !stats ? <p>שגיאה בטעינת נתונים</p> : (
          <>
            {/* Main stats */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
              <StatCard label="בניינים" value={stats.totalBuildings} color="#1a4a8a" />
              <StatCard label="דירות" value={stats.totalApartments} color="#1a4a8a" />
              <StatCard label="בעלי זכויות (בשלמות או חלקי)" value={stats.statusBreakdown ? stats.statusBreakdown.reduce((s, r) => s + r.tenantCount, 0) : stats.totalTenants} color="#1a4a8a" />
              <StatCard label="יחידת דיור ציבורי" value={stats.publicHousingCount} color="#7c3aed" />
              {(() => {
                const signedTotal = (stats.pure7Count || 0) + (stats.purePublicCount || 0) + (stats.mixed89Count || 0);
                return (<>
                  <StatCard
                    label="חתומים + דיור ציבורי"
                    value={signedTotal}
                    color="#16a34a"
                    sub={`מתוך ${stats.totalApartments} דירות`}
                  />
                  <StatCard
                    label="אחוז חתימות"
                    value={`${pct.toFixed(2)}%`}
                    color={pctColor}
                    sub={`${signedTotal} מתוך ${stats.totalApartments} דירות | ${pct >= 66.67 ? 'עמד ביעד' : `חסר ${(66.67 - pct).toFixed(2)}% ליעד`}`}
                  />
                </>);
              })()}
              {(() => {
                const pure7 = stats.pure7Count || 0;
                const purePub = stats.purePublicCount || 0;
                const mixed89 = stats.mixed89Count || 0;
                const total = stats.totalApartments;
                const signedTotal = pure7 + purePub;
                return (<>
                  <StatCard
                    label='אחוז חתימות על הסכם סופי + דיור ציבורי'
                    value={`${(Math.trunc(signedTotal / total * 10000) / 100).toFixed(2)}%`}
                    color="#16a34a"
                    sub={`${signedTotal} מתוך ${total} | הסכם סופי: ${pure7} | דיור ציבורי: ${purePub}`}
                  />
                  <StatCard
                    label='חתימות לטיפול (חלקיות ו/או עם בעיית רישום)'
                    value={`${(Math.trunc(mixed89 / total * 10000) / 100).toFixed(2)}%`}
                    color="#ca8a04"
                    sub={`${mixed89} מתוך ${total}`}
                  />
                </>);
              })()}
              {stats.overdueFollowUps > 0 && (
                <StatCard
                  label="מעקבים לטיפול"
                  value={stats.overdueFollowUps}
                  color="#dc2626"
                  sub="דירות עם תאריך מעקב שעבר"
                />
              )}
            </div>

            {/* Progress bar */}
            <div style={{ background: 'white', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #e8ecf0', marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, color: '#374151' }}>התקדמות לעבר היעד</span>
                <span style={{ fontWeight: 700, color: pctColor }}>{pct.toFixed(2)}%</span>
              </div>
              <div style={{ position: 'relative', marginTop: 8, marginBottom: 28 }}>
                <div style={{ background: '#f0f2f5', borderRadius: 8, height: 20, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: pctColor, borderRadius: 8, transition: 'width 0.5s ease' }} />
                </div>
                {/* logo at end of green bar */}
                <div className="progress-badge" style={{ position: 'absolute', top: '50%', right: `${Math.min(pct, 100)}%`, transform: 'translate(50%, -50%)', width: 52, height: 52, borderRadius: '50%', background: 'white', border: '2px solid #16a34a', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  <img src={'/logos/community_icon.png'} alt="logo" style={{ width: '65%', height: '65%', objectFit: 'contain' }} />
                </div>
                {/* vertical line at 66.67% */}
                <div style={{ position: 'absolute', right: '66.67%', top: 0, width: 2, height: '100%', background: '#1a4a8a', zIndex: 2 }} />
                {/* label below the line */}
                <div style={{ position: 'absolute', right: '66.67%', top: '100%', transform: 'translateX(50%)', textAlign: 'center', whiteSpace: 'nowrap', marginTop: 16 }}>
                  <span style={{ fontSize: '0.72rem', color: '#1a4a8a', fontWeight: 700 }}>66.67% — אחוז ההסכמה הנדרש על פי חוק</span>
                </div>
              </div>
            </div>

            {/* Status breakdown table */}
            {stats.statusBreakdown && stats.statusBreakdown.length > 0 && (
              <div style={{ background: 'white', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #e8ecf0', marginBottom: 24 }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 700, color: '#374151' }}>פירוט חלק בדירה לפי סטטוס</h3>
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                  <thead>
                    <tr style={{ background: '#1B2A4A', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'right', padding: '10px 12px', color: 'white', fontWeight: 700 }}>סטטוס</th>
                      <th style={{ textAlign: 'center', padding: '10px 12px', color: 'white', fontWeight: 700 }}>בעלי זכויות</th>
                      <th style={{ textAlign: 'center', padding: '10px 12px', color: 'white', fontWeight: 700 }}>אחוז מסך הכל</th>
                      <th style={{ textAlign: 'center', padding: '10px 12px', color: 'white', fontWeight: 700 }}>סכום חלק בדירה</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const totalCount = stats.statusBreakdown.reduce((s, r) => s + r.tenantCount, 0);
                      return stats.statusBreakdown.map(row => (
                        <tr key={row.status} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 12px', color: '#374151' }}>{STATUS_LABELS[row.status] || `סטטוס ${row.status}`}</td>
                          <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                            <button onClick={() => openStatusModal(row.status, STATUS_LABELS[row.status] || `סטטוס ${row.status}`)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a4a8a', fontWeight: 700, fontSize: '1rem', textDecoration: 'underline', padding: 0 }}>
                              {row.tenantCount}
                            </button>
                          </td>
                          <td style={{ textAlign: 'center', padding: '10px 12px', color: '#6b7280' }}>{totalCount > 0 ? (row.tenantCount / totalCount * 100).toFixed(1) + '%' : '—'}</td>
                          <td style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 700, color: '#1a4a8a' }}>{row.partSum.toFixed(2)}</td>
                        </tr>
                      ));
                    })()}
                    <tr style={{ borderTop: '2px solid #e5e7eb', background: '#f9fafb' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 800, color: '#374151' }}>סך הכל</td>
                      <td style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 800, color: '#374151' }}>
                        {stats.statusBreakdown.reduce((s, r) => s + r.tenantCount, 0)}
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 800, color: '#374151' }}>100%</td>
                      <td style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 800, color: '#1a4a8a' }}>
                        {stats.statusBreakdown.reduce((s, r) => s + r.partSum, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {modal && (
        <div onClick={() => setModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, padding: 28, width: '90%', maxWidth: 700, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#1a4a8a' }}>{modal.statusLabel}</div>
                {!modal.loading && <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 2 }}>{modal.tenants.length} דיירים</div>}
              </div>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {modal.loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>טוען...</div>
              ) : modal.tenants.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>אין דיירים</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                  <thead>
                    <tr style={{ background: '#1B2A4A' }}>
                      <th style={{ textAlign: 'right', padding: '8px 10px', color: 'white', fontWeight: 700 }}>שם</th>
                      <th style={{ textAlign: 'center', padding: '8px 10px', color: 'white', fontWeight: 700 }}>טלפון</th>
                      <th style={{ textAlign: 'center', padding: '8px 10px', color: 'white', fontWeight: 700 }}>כתובת</th>
                      <th style={{ textAlign: 'center', padding: '8px 10px', color: 'white', fontWeight: 700 }}>דירה</th>
                      <th style={{ textAlign: 'center', padding: '8px 10px', color: 'white', fontWeight: 700 }}>% בעלות</th>
                      <th style={{ textAlign: 'center', padding: '8px 10px', color: 'white', fontWeight: 700 }}>מעבר לבניין</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modal.tenants.map((t, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                        <td style={{ padding: '8px 10px', fontWeight: 600 }}>{t.firstName} {t.lastName}</td>
                        <td style={{ textAlign: 'center', padding: '8px 10px', color: '#6b7280' }}>{t.cellularPhone}</td>
                        <td style={{ textAlign: 'center', padding: '8px 10px', color: '#6b7280' }}>{t.buildingAddress} בניין {t.buildingNumber}</td>
                        <td style={{ textAlign: 'center', padding: '8px 10px', color: '#6b7280' }}>{t.apartmentNumber}</td>
                        <td style={{ textAlign: 'center', padding: '8px 10px', color: '#1a4a8a', fontWeight: 700 }}>
                          {t.percentageOfHOldingInApartment != null ? (parseFloat(t.percentageOfHOldingInApartment) * 100).toFixed(0) + '%' : '—'}
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px 10px' }}>
                          <button onClick={() => {
                            sessionStorage.setItem(`building_addr_${projectName}`, t.buildingAddress);
                            sessionStorage.setItem(`building_num_${projectName}`, String(t.buildingNumber));
                            navigate(`/buildings/${encodeURIComponent(projectName)}`);
                          }}
                            style={{ background: '#1B2A4A', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            לחץ כאן
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
